import { lazy, ComponentType } from 'react';
import { attemptSelfHealReload } from './staleTabSelfHeal';

/**
 * A wrapper around React.lazy that adds retry logic for failed chunk loads.
 * This handles ChunkLoadError which occurs when webpack fails to load a lazy
 * chunk (e.g., due to network issues, navigation during streaming, or a
 * rolling deploy).
 *
 * What retries can and cannot fix: every retry re-requests the SAME chunk
 * URL after a plain delay — there is no cache-busting here (an earlier
 * edition of this file claimed one that never existed). Retries therefore
 * only ride out transient network blips. A PRUNED chunk — deploy.yml deletes
 * hashed /static/js/*.js past retention, after which the CDN serves
 * index.html as 200 text/html — fails identically on every retry; the only
 * fix is reloading fresh HTML, which is delegated to staleTabSelfHeal, the
 * one owner of recovery-reload policy (manifest-gated so offline never
 * reloads, loop-guarded, streaming-lease aware).
 *
 * @param importFn - The dynamic import function (e.g., () => import('./MyComponent'))
 * @param retries - Number of retry attempts (default: 3)
 * @param retryDelay - Delay between retries in ms (default: 1000)
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  retryDelay = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn, retries, retryDelay));
}

/** Latch key from the retired reload-once scheme. Never written any more;
 * removed on every successful import so tabs latched by it heal. */
const LEGACY_RELOAD_LATCH_KEY = 'olivia__chunk_reload_attempted';

/** When this module last handled a chunk-classified failure. A timestamp
 * window rather than a boolean because the global unhandledrejection event
 * can arrive AFTER retryImport's final throw. */
let lastRecoveryActivityAt = 0;

/**
 * Whether lazyWithRetry recently owned a chunk failure. App.tsx's global
 * unhandledrejection handler suppresses chunk errors only while this reads
 * true — a chunk rejection with no recovery in flight (a bare import()?) is
 * a real failure that must stay loud.
 */
export function chunkRecoveryRecentlyActive(windowMs = 30_000): boolean {
  return Date.now() - lastRecoveryActivityAt < windowMs;
}

// Chunk-class = worth retrying AND worth a recovery reload. Broad message
// matching is safe HERE because it is scoped to an actual import attempt —
// the same strings are far too broad for a global rejection filter.
function isChunkClassError(error: any): boolean {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('network') ||
    error?.message?.includes('Unexpected token') || // HTML served where JS was expected
    error?.message?.includes('HTML')
  );
}

/**
 * The retry-then-heal policy for ANY dynamic import. Production callers:
 * lazyWithRetry (route components through React.lazy) and useDeferredModule
 * (modules loaded after first paint WITHOUT React.lazy — the chat engine and
 * the deferred routes). Generic over the module's shape because the chat
 * engine exports a named component, not a default one.
 */
export async function retryImport<M>(
  importFn: () => Promise<M>,
  retries: number,
  retryDelay: number
): Promise<M> {
  let lastError: any;
  let chunkClass = false;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) {
        // Plain wait, then the same URL again — transient blips only (see
        // the header note; a pruned chunk needs the reload path below).
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
      const mod = await importFn();
      try {
        sessionStorage.removeItem(LEGACY_RELOAD_LATCH_KEY);
      } catch {
        /* storage unusable — nothing to heal */
      }
      return mod;
    } catch (error: any) {
      lastError = error;
      chunkClass = isChunkClassError(error);
      // A non-chunk error is the module's own bug: never retried, never
      // reloaded — it goes straight to the error boundary.
      if (!chunkClass) break;
      lastRecoveryActivityAt = Date.now();
      if (attempt === retries) break;
      console.warn(
        `Chunk load failed (attempt ${attempt + 1}/${retries + 1}), retrying...`,
        error?.message
      );
    }
  }

  if (chunkClass) {
    console.error('Failed to load chunk after retries:', lastError?.message);
    // ALL reload policy lives in staleTabSelfHeal: manifest reachability
    // (offline refuses), timestamped loop guards, per-tab cap, streaming
    // lease. It may resolve true with the reload lease-deferred — the
    // pending lazy promise below keeps the Suspense fallback up meanwhile.
    const healed = await attemptSelfHealReload('chunk-failure');
    if (healed) {
      // Reload committed. Hold the promise so Suspense — not the error
      // boundary — shows while the page tears down; the 10s escape hatch
      // still reaches the boundary if the reload was somehow blocked.
      await new Promise((resolve) => setTimeout(resolve, 10_000));
    }
    lastRecoveryActivityAt = Date.now();
    try {
      // Marks the error for ErrorBoundary's reload-first chunk card: a
      // React.lazy rejection is cached for the module lifetime, so a
      // boundary reset cannot revive it — only a reload can.
      lastError.__prest0ChunkRecoveryExhausted = true;
    } catch {
      /* frozen/exotic error object — the boundary also matches name/message */
    }
  }

  throw lastError;
}

export default lazyWithRetry;
